import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useRef, useState } from "react";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { OutlinePass } from "three/examples/jsm/postprocessing/OutlinePass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import { Html } from "@react-three/drei";
import { useMusicPlaybackStore } from "../../store";

function OutlineEffect({ selected, interactableMeshes, hoverText, musicModelId }) {
  const isPlaying = useMusicPlaybackStore((s) => musicModelId ? s.isPlaying(musicModelId) : false);
  const displayText = musicModelId
    ? (isPlaying ? "Click to pause music" : "Click to play music")
    : hoverText;
  const { scene, camera, gl, size } = useThree();
  const composer = useRef();
  const outlinePassRef = useRef();
  const selectedRef = useRef([]);
  const interactableRef = useRef([]);
  const dprScaleRef = useRef(1);
  const [avgPosition, setAvgPosition] = useState([0, 0, 0]);

  // Keep refs in sync with props
  selectedRef.current = selected;
  interactableRef.current = interactableMeshes;

  // Create composer & passes once
  useEffect(() => {
    // When the Canvas caps DPR (e.g. dpr={[1,2]}), the outline renders at
    // fewer, larger pixels. Scale parameters so the outline appears the same
    // physical size it did at the device's native DPR.
    const dprScale = gl.getPixelRatio() / window.devicePixelRatio;
    dprScaleRef.current = dprScale;

    const renderPass = new RenderPass(scene, camera);
    const outlinePass = new OutlinePass(
      new THREE.Vector2(size.width, size.height),
      scene,
      camera
    );

    outlinePass.pulsePeriod = 0;

    const comp = new EffectComposer(gl);
    comp.addPass(renderPass);
    comp.addPass(outlinePass);
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      0.05,
      0.75,
      0.99
    );
    comp.addPass(bloomPass);
    composer.current = comp;
    outlinePassRef.current = outlinePass;

    return () => {
      // Dispose old composer on re-render / HMR to prevent multiple
      // composers stacking and doubling the outline brightness.
      comp.dispose();
    };
  }, [scene, camera, gl, size]);

  // Compute hover text position when selected meshes change
  useEffect(() => {
    if (!outlinePassRef.current) return;
    if (Array.isArray(selected) && selected.length > 0) {
      const meshes = selected.filter((obj) => obj.isMesh);
      if (meshes.length > 0) {
        const sum = new THREE.Vector3();
        meshes.forEach((mesh) => {
          const worldPos = new THREE.Vector3();
          mesh.getWorldPosition(worldPos);
          sum.add(worldPos);
        });
        sum.divideScalar(meshes.length);
        setAvgPosition([sum.x, sum.y, sum.z]);
      }
    } else {
      setAvgPosition([0, 0, 0]);
    }
  }, [selected]);

  useFrame(({ clock }) => {
    if (!composer.current || !outlinePassRef.current) return;
    const pass = outlinePassRef.current;
    const sel = selectedRef.current;
    const inter = interactableRef.current;
    const dpr = dprScaleRef.current;

    if (Array.isArray(sel) && sel.length > 0) {
      // Green mode — hovered object
      pass.visibleEdgeColor.set("green");
      pass.hiddenEdgeColor.set("green");
      pass.edgeStrength = 20 * dpr;
      pass.edgeGlow = 5 * dpr;
      pass.edgeThickness = 6 * dpr;
      pass.pulsePeriod = 0;
      pass.selectedObjects = sel.filter((o) => o.isMesh);
    } else if (Array.isArray(inter) && inter.length > 0) {
      // Blue mode — thin, crisp pulsing outline matching decoration FrontEdges aesthetic
      // Decorations use FrontEdges (geometric edge lines) with opacity pulsing.
      // We approximate that look: no glow, thin edges, pulse edgeStrength like opacity.
      const pulse = 0.4 + Math.sin(clock.getElapsedTime() * 2) * 0.3;
      pass.visibleEdgeColor.set("#60a5fa");
      pass.hiddenEdgeColor.set(0x000000);
      pass.edgeStrength = 25 * dpr * pulse;
      pass.edgeGlow = 1 * dpr;
      pass.edgeThickness = 7 * dpr;
      pass.pulsePeriod = 0;
      pass.selectedObjects = inter;
    } else {
      pass.selectedObjects = [];
    }

    composer.current.render();
  }, 1);

  if (displayText) {
    return (
      <Html
        position={avgPosition}
        center
        distanceFactor={8}
        style={{
          color: "white",
          fontFamily: "Arial, sans-serif",
          textAlign: "center",
          padding: "10px",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          borderRadius: "5px",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            fontWeight: 400,
            whiteSpace: "nowrap",
          }}
        >
          <p>{displayText}</p>
        </div>
      </Html>
    );
  }
  return null;
}

export default OutlineEffect;
